import { useState } from 'react';
import { Clock, Copy, Check, Eye, CheckCircle2 } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import { FindingKindBadge } from './FindingKindBadge';
import { FindingStatusBadge, isFindingStale } from './FindingStatusBadge';
import { formatPaiseToRupees } from '../schemas';
import type { ReconciliationFindingRow } from '../types';

interface FindingsTableProps {
  findings: ReconciliationFindingRow[];
  onInspect: (finding: ReconciliationFindingRow) => void;
  onResolve: (finding: ReconciliationFindingRow) => void;
}

function parseStateSummary(jsonStr: string): string {
  try {
    const obj = JSON.parse(jsonStr);
    const status = obj.status ?? obj.paymentStatus;
    const amount = obj.amountPaise ?? obj.amount;
    const parts: string[] = [];
    if (status) parts.push(String(status));
    if (typeof amount === 'number') parts.push(formatPaiseToRupees(amount));
    return parts.length > 0 ? parts.join(' • ') : 'Available';
  } catch {
    return 'Details stored';
  }
}

export function FindingsTable({
  findings,
  onInspect,
  onResolve,
}: FindingsTableProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const formatRelativeTime = (isoString: string): string => {
    const ms = Date.now() - new Date(isoString).getTime();
    const minutes = Math.floor(ms / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (findings.length === 0) {
    return (
      <div className="p-12 text-center border border-border-subtle rounded-2xl glass-panel">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3 opacity-70" />
        <h4 className="text-sm font-bold text-on-surface">Queue is balanced</h4>
        <p className="text-xs text-on-surface-variant mt-1">
          No reconciliation findings match the active queue and filter criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border-subtle rounded-2xl overflow-hidden glass-panel">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-border-subtle bg-surface-container/40 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <th className="py-3 px-4">Kind</th>
              <th className="py-3 px-4">Entity Reference</th>
              <th className="py-3 px-4">Detected</th>
              <th className="py-3 px-4">Internal State</th>
              <th className="py-3 px-4">Gateway State</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle/50">
            {findings.map((finding) => {
              const stale = isFindingStale(finding);
              return (
                <tr
                  key={finding.id}
                  className={cn(
                    'hover:bg-white/[0.02] transition-colors group',
                    stale && 'bg-rose-500/[0.03]',
                  )}
                >
                  {/* Kind */}
                  <td className="py-3 px-4 whitespace-nowrap">
                    <FindingKindBadge kind={finding.kind} />
                  </td>

                  {/* Entity Reference */}
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-on-surface">{finding.entityType}:</span>
                      <span className="font-mono text-on-surface-variant truncate max-w-[120px] sm:max-w-[160px]">
                        {finding.entityId}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyId(finding.entityId)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-primary transition-all text-on-surface-variant"
                        title="Copy entity ID"
                      >
                        {copiedId === finding.entityId ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </td>

                  {/* Detected At */}
                  <td className="py-3 px-4 whitespace-nowrap">
                    <div className="flex items-center gap-1.5" title={new Date(finding.detectedAt).toLocaleString('en-IN')}>
                      <Clock className={cn('w-3.5 h-3.5', stale ? 'text-rose-400' : 'text-on-surface-variant')} />
                      <span className={cn('font-mono', stale ? 'text-rose-400 font-semibold' : 'text-on-surface-variant')}>
                        {formatRelativeTime(finding.detectedAt)}
                      </span>
                    </div>
                  </td>

                  {/* Internal State Summary */}
                  <td className="py-3 px-4">
                    <span className="font-mono text-on-surface bg-surface-container/60 px-2 py-0.5 rounded border border-border-subtle">
                      {parseStateSummary(finding.internalState)}
                    </span>
                  </td>

                  {/* Gateway State Summary */}
                  <td className="py-3 px-4">
                    <span className="font-mono text-cyan-300 bg-cyan-950/20 px-2 py-0.5 rounded border border-cyan-800/30">
                      {parseStateSummary(finding.gatewayState)}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-3 px-4 whitespace-nowrap">
                    <FindingStatusBadge row={finding} />
                  </td>

                  {/* Actions */}
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        data-testid="inspect-finding-btn"
                        onClick={() => onInspect(finding)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border-subtle text-on-surface-variant hover:text-on-surface hover:bg-white/5 transition-all"
                        title="Inspect diff and details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect</span>
                      </button>

                      {!finding.resolvedAt && (
                        <button
                          type="button"
                          data-testid="resolve-finding-btn"
                          onClick={() => onResolve(finding)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 font-semibold transition-all"
                          title="Resolve finding"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Resolve</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
